import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Layout, Table, Input, Button, Form, Space, Row, Col,
    Breadcrumb, Modal, message, Typography, Tooltip, Select, Tag
} from "antd";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import {
    PlusOutlined, HomeOutlined, HistoryOutlined, CheckOutlined, CloseOutlined,
    SearchOutlined, CalculatorOutlined
} from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import Highlighter from "react-highlight-words";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;
const { Option } = Select;

const ManageInventoryAdjustments = () => {
    const [adjustments, setAdjustments] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isApproveModalVisible, setIsApproveModalVisible] = useState(false);
    const [currentAdj, setCurrentAdj] = useState(null);
    const [addForm] = Form.useForm();
    const [appForm] = Form.useForm();

    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef(null);

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };
    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button type="primary" onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Search</Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>Reset</Button>
                    <Button type="link" size="small" onClick={() => { confirm({ closeDropdown: false }); setSearchText(selectedKeys[0]); setSearchedColumn(dataIndex); }}>Filter</Button>
                    <Button type="link" size="small" onClick={() => close()}>close</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
        onFilter: (value, record) => record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
        filterDropdownProps: { onOpenChange(open) { if (open) setTimeout(() => searchInput.current?.select(), 100); } },
        render: (text) => searchedColumn === dataIndex ? (
            <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                searchWords={[searchText]} autoEscape
                textToHighlight={text ? text.toString() : ''}
            />
        ) : text,
    });

    const fetchItems = useCallback(async () => {
        try {
            const res = await api.get('/inventory/items/list.php?page=1&limit=200');
            setItems(res.data.dataset);
        } catch {
            message.error("Failed to load inventory items.");
        }
    }, []);

    const fetchAdjustments = useCallback(async (page = 1, pageSize = 10) => {
        try {
            setLoading(true);
            const res = await api.get(`/inventory/adjustments/list.php?page=${page}&limit=${pageSize}`);
            setAdjustments(res.data.dataset);
            setPagination({
                current: res.data.pagination?.current_page || 1,
                pageSize: res.data.pagination?.per_page || 10,
                total: res.data.pagination?.total_records || 0,
            });
        } catch {
            message.error("Failed to fetch inventory adjustments.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAdjustments(1, 10);
        fetchItems();
    }, [fetchAdjustments, fetchItems]);

    const handleTableChange = (pg) => fetchAdjustments(pg.current, pg.pageSize);

    const handleItemChange = (itemId) => {
        const selected = items.find(i => parseInt(i.invi_id) === itemId);
        if (selected) {
            addForm.setFieldsValue({
                unit_cost: selected.cost_price,
            });
        }
    };

    const handleAdd = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/inventory/adjustments/add.php', {
                invi_id: values.invi_id,
                movement_type: values.movement_type,
                qty: values.qty,
                unit_cost: values.unit_cost,
                resaon: values.resaon, // using resaon spelling as defined in DB schema
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Adjustment request submitted successfully.");
                setIsAddModalVisible(false);
                fetchAdjustments(pagination.current, pagination.pageSize);
                addForm.resetFields();
                fetchItems(); // refresh items (qtys might change later on approval, but good to fetch)
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to submit adjustment request.");
        } finally { setLoading(false); }
    };

    const openApproveModal = (record) => {
        setCurrentAdj(record);
        appForm.resetFields();
        setIsApproveModalVisible(true);
    };

    const handleApproveReject = async (action) => {
        try {
            setLoading(true);
            const comment = appForm.getFieldValue('comment');
            const res = await api.post('/inventory/adjustments/approve.php', {
                im_id: currentAdj.im_id,
                action: action,
                comment: comment,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message);
                setIsApproveModalVisible(false);
                fetchAdjustments(pagination.current, pagination.pageSize);
                fetchItems();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || `Failed to ${action.toLowerCase()} adjustment.`);
        } finally { setLoading(false); }
    };

    const columns = [
        {
            title: 'Date', dataIndex: 'cr_dt', key: 'cr_dt', width: 140,
            render: (val) => new Date(val).toLocaleString(),
        },
        {
            title: 'Item Code', dataIndex: 'inv_code', key: 'inv_code', width: 100,
            ...getColumnSearchProps('inv_code'),
        },
        {
            title: 'Description', dataIndex: 'inv_desc', key: 'inv_desc',
            ...getColumnSearchProps('inv_desc'),
        },
        {
            title: 'Type', dataIndex: 'movement_type', key: 'movement_type', width: 90,
            render: (type) => {
                let color = 'blue';
                if (['IN', 'RECEIVE', 'RESTOCK', 'ADD'].includes(type.toUpperCase())) color = 'green';
                if (['OUT', 'DAMAGE', 'DISPATCH', 'REDUCE', 'USE'].includes(type.toUpperCase())) color = 'volcano';
                return <Tag color={color}>{type.toUpperCase()}</Tag>;
            }
        },
        {
            title: 'Qty', dataIndex: 'qty', key: 'qty', align: 'center', width: 70,
        },
        {
            title: 'Total Cost', dataIndex: 'total_cost', key: 'total_cost', align: 'right',
            render: (val) => `LKR ${parseFloat(val).toFixed(2)}`,
        },
        {
            title: 'Reason', dataIndex: 'resaon', key: 'resaon',
            ...getColumnSearchProps('resaon'),
        },
        {
            title: 'Created By', dataIndex: 'creator_name', key: 'creator_name',
        },
        {
            title: 'Status', dataIndex: 'status', key: 'status', width: 110,
            align: 'center',
            render: (status) => {
                if (status == 0) return <Tag color="gold">Pending</Tag>;
                if (status == 1) return <Tag color="success">Approved</Tag>;
                return <Tag color="error">Rejected</Tag>;
            }
        },
        {
            title: 'Action', key: 'action', align: 'center', fixed: 'right', width: 100,
            render: (_, record) => {
                if (record.status == 0) {
                    return (
                        <Button size="small" type="primary" onClick={() => openApproveModal(record)}>
                            Process
                        </Button>
                    );
                }
                return (
                    <Tooltip title={record.app_comment ? `Approver comment: ${record.app_comment}` : "No comment"}>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {record.approver_name || 'System'}
                        </Typography.Text>
                    </Tooltip>
                );
            }
        },
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between">
                    <Col>
                                                    <Breadcrumb
                                className="fluid-breadcrumb"
                                items={[
                                    { href: '/dash', title: <HomeOutlined /> },
                                    { href: '/inventory-items', title: 'Inventory' },
                                    { title: 'Stock Adjustments' },
                                ]}
                            />
                        <h1 className="fluid-page-title">
                            <HistoryOutlined /> Stock Adjustments
                        </h1>
                    </Col>
                    <Col xs={0} md={12} style={{ textAlign: 'right' }}>
                        <p className="fluid-page-subtitle">Request and approve stock movements (In, Out, Damage)</p>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    <div className="fluid-card-header">
                        <h3 className="fluid-card-title">Adjustments History</h3>
                        <Space>
                            <Typography.Text type="secondary">{pagination.total} records</Typography.Text>
                            <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsAddModalVisible(true)}>
                                New Adjustment
                            </Button>
                        </Space>
                    </div>
                    <div className="fluid-table">
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={adjustments}
                            rowKey="im_id"
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                            }}
                            onChange={handleTableChange}
                            loading={loading}
                            scroll={{ x: 1100 }}
                        />
                    </div>
                </div>
            </Content>

            <Footerdiv />

            <Modal
                title="Request Stock Adjustment / Movement"
                open={isAddModalVisible}
                onCancel={() => { setIsAddModalVisible(false); addForm.resetFields(); }}
                footer={null}
                width={700}
                destroyOnClose
            >
                <Form form={addForm} layout="vertical" onFinish={handleAdd}>
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label="Inventory Item" name="invi_id" rules={[{ required: true, message: 'Item required' }]}>
                                <Select allowClear placeholder="Select Stock Item" onChange={handleItemChange} showSearch optionFilterProp="children">
                                    {items.map(itm => (
                                        <Option key={itm.invi_id} value={parseInt(itm.invi_id)}>
                                            {itm.inv_code} - {itm.inv_desc} (Avail: {itm.available_qty})
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Movement Type" name="movement_type" rules={[{ required: true, message: 'Movement type required' }]}>
                                <Select allowClear placeholder="Select Type">
                                    <Option value="IN">Stock In (Purchase/Receive)</Option>
                                    <Option value="OUT">Stock Out (Usage/Dispatch)</Option>
                                    <Option value="DAMAGE">Damaged / Disposed</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Quantity" name="qty" rules={[{ required: true, message: 'Quantity required' }]}>
                                <Input type="number" min="1" placeholder="10" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label="Unit Cost (LKR)" name="unit_cost" rules={[{ required: true, message: 'Unit cost required' }]}>
                                <Input type="number" step="0.01" min="0" placeholder="150.00" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={16}>
                            <Form.Item label="Reason for adjustment" name="resaon" rules={[{ required: true, message: 'Reason required' }]}>
                                <Input placeholder="Restocking for season / Fertilizer applied to Field A / Bag damaged by rain..." />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row justify="end">
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button danger onClick={() => { setIsAddModalVisible(false); addForm.resetFields(); }}>Cancel</Button>
                                <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading}>Submit Request</Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>

            <Modal
                title="Process Stock Adjustment Request"
                open={isApproveModalVisible}
                onCancel={() => setIsApproveModalVisible(false)}
                footer={null}
                width={500}
            >
                {currentAdj && (
                    <div style={{ marginBottom: 20 }}>
                        <p><strong>Item Code:</strong> {currentAdj.inv_code}</p>
                        <p><strong>Movement Type:</strong> {currentAdj.movement_type}</p>
                        <p><strong>Quantity:</strong> {currentAdj.qty}</p>
                        <p><strong>Total Cost:</strong> LKR {parseFloat(currentAdj.total_cost).toFixed(2)}</p>
                        <p><strong>Reason:</strong> {currentAdj.resaon}</p>
                        <hr />
                    </div>
                )}
                <Form form={appForm} layout="vertical">
                    <Form.Item label="Approval/Rejection Comment" name="comment">
                        <Input.TextArea placeholder="Enter comment/reason..." rows={3} />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space>
                            <Button type="primary" icon={<CheckOutlined />} loading={loading} onClick={() => handleApproveReject('APPROVE')} style={{ backgroundColor: 'green', borderColor: 'green' }}>
                                Approve
                            </Button>
                            <Button type="primary" danger icon={<CloseOutlined />} loading={loading} onClick={() => handleApproveReject('REJECT')}>
                                Reject
                            </Button>
                            <Button danger onClick={() => setIsApproveModalVisible(false)}>Cancel</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default ManageInventoryAdjustments;
